import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService, User } from '../services/auth.service';
import { setSampleAuthForLocalMode } from '../services/systems/sample-local/sampleLocalMode';
import { isPublicAppPath } from '../utils/publicAccessPaths';

/** JWT の roleId と /auth/me の role.id の両方を考慮（DB とトークンが一瞬ずれる場合のフォールバック） */
export function isAdminUser(user: User | null | undefined): boolean {
  if (!user) return false;
  const fromToken = Number(user.roleId);
  const fromRole = user.role?.id != null ? Number(user.role.id) : NaN;
  return fromToken === 1 || fromRole === 1;
}

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasToken, setHasToken] = useState(() => !!localStorage.getItem('token'));
  const queryClient = useQueryClient();

  // トークンの存在を監視（localStorageの変更を検知）
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('token');
      setHasToken(!!token);
    };
    
    // 初回チェック
    checkToken();
    
    // storageイベントをリッスン（別タブでの変更を検知）
    window.addEventListener('storage', checkToken);
    
    // 定期的にチェック（同じタブでの変更を検知）
    const interval = setInterval(checkToken, 1000);
    
    return () => {
      window.removeEventListener('storage', checkToken);
      clearInterval(interval);
    };
  }, []);

  const { data: userResponse, isLoading, error: authError } = useQuery<User>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const response = await authService.getMe();
      return response.data.user;
    },
    enabled: hasToken, // 動的に更新されるトークンの存在をチェック
    retry: (failureCount, error: any) => {
      // 401エラーの場合はリトライしない（トークンが無効）
      if (error?.response?.status === 401) {
        return false;
      }
      // 500/503エラーの場合は最大2回リトライ（サーバーエラーの可能性があるため）
      if (error?.response?.status === 500 || error?.response?.status === 503) {
        return failureCount < 2;
      }
      // その他のエラーは最大1回リトライ
      return failureCount < 1;
    },
    retryDelay: 1000, // 1秒後にリトライ
    staleTime: 1000 * 60 * 5, // 5分間はキャッシュを有効にする
    gcTime: 1000 * 60 * 10, // 10分間キャッシュを保持（React Query v5ではcacheTimeからgcTimeに変更）
    refetchOnWindowFocus: true, // ウィンドウフォーカス時に認証状態を確認
    refetchOnMount: true, // マウント時に認証状態を確認
    refetchOnReconnect: true, // 再接続時に認証状態を確認
    // 定期的に認証状態をチェック（5分ごと）
    refetchInterval: 1000 * 60 * 5, // 5分ごとに認証状態をチェック
    refetchIntervalInBackground: false, // バックグラウンドではチェックしない（パフォーマンスのため）
  });

  // React Query v5ではonSuccess/onErrorが削除されたため、useEffectで処理
  useEffect(() => {
    if (userResponse) {
      setIsAuthenticated(true);
    }
  }, [userResponse]);

  /**
   * サンプル管理の正本 preference は authenticated フラグに依存する。
   * SampleZipModeShell 外（/reports 等）でもトークン有無で同期しないと、
   * リロード時に誤って local 扱いになり DB 専用画面から弾かれる。
   * （クラウド DB の行データには触れない・preference 解決のみ）
   */
  useEffect(() => {
    setSampleAuthForLocalMode(hasToken);
  }, [hasToken]);

  useEffect(() => {
    if (authError) {
      const status = (authError as any)?.response?.status;
      const currentPath = window.location.pathname;
      
      // 401エラーの場合、トークンが無効または期限切れ
      if (status === 401) {
        // トークンが存在する場合は削除
        if (localStorage.getItem('token')) {
          console.warn('Authentication failed: Token expired or invalid.');
          setIsAuthenticated(false);
          localStorage.removeItem('token');
          setHasToken(false);
          setSampleAuthForLocalMode(false);

          // 公開ページ（トップ・/en 等）は api.ts と同様に /login へ飛ばさずトークンだけ削除
          if (currentPath !== '/login' && !isPublicAppPath(currentPath)) {
            setTimeout(() => {
              window.location.href = '/login';
            }, 100);
          }
        }
      } else if (status === 500 || status === 503) {
        // 500/503エラーの場合、サーバーエラーの可能性があるため、トークンを削除しない
        // ただし、認証状態はリセットする
        // リトライはReact Queryのretry設定に任せる
        console.warn(`Authentication error (${status}): Server error. Retrying...`);
        setIsAuthenticated(false);
        // トークンは保持する（サーバーが復旧した場合に再認証できるように）
      }
    }
  }, [authError]);

  const user: User | undefined = userResponse;

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: async (result) => {
      if (result?.data?.token) {
        localStorage.setItem('token', result.data.token);
        setHasToken(true);
        const meResponse = await authService.getMe();
        queryClient.setQueryData(['auth', 'me'], meResponse.data.user);
        setIsAuthenticated(true);
      }
    },
    onError: () => {
      setIsAuthenticated(false);
      localStorage.removeItem('token');
      setHasToken(false); // トークンの存在を更新
    },
  });

  const completeLogin = async (token: string) => {
    localStorage.setItem('token', token);
    setHasToken(true);
    const meResponse = await authService.getMe();
    queryClient.setQueryData(['auth', 'me'], meResponse.data.user);
    setIsAuthenticated(true);
  };

  const login = async (
    data: Parameters<typeof authService.login>[0],
    options?: {
      onSuccess?: () => void;
      onError?: (error: any) => void;
    }
  ) => {
    try {
      const result = await loginMutation.mutateAsync(data);
      options?.onSuccess?.();
      return result;
    } catch (error) {
      options?.onError?.(error);
      throw error;
    }
  };

  const logoutMutation = useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      setIsAuthenticated(false);
      queryClient.clear();
      localStorage.removeItem('token');
      setHasToken(false); // トークンの存在を更新
    },
  });

  useEffect(() => {
    // トークンが存在し、userResponseが取得できている場合は認証済み
    const token = localStorage.getItem('token');
    if (token && userResponse) {
      setIsAuthenticated(true);
    } else if (token && !authError) {
      // トークンはあるが、userResponseがまだ取得できていない場合
      // 401エラーがない限り、認証済みとして扱う（ログイン直後など）
      setIsAuthenticated(true);
    } else if (!token) {
      setIsAuthenticated(false);
    }
  }, [user, userResponse, authError]);

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout: () => logoutMutation.mutate(),
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    completeLogin,
  };
}

